// The canonical data now lives in lib/locations.ts (pure, testable, and
// reachable by the node --test glob). Re-exported here so the existing import
// sites keep working.
export {
  SLUG_TO_LOCATION, LOCATION_TO_SLUG, LOCATIONS,
  isKnownLocation, otherLocation, shortLocationName,
} from '../lib/locations';

import { SLUG_TO_LOCATION, LOCATION_TO_SLUG, isKnownLocation } from '../lib/locations';

/**
 * Make `location` the browser's active location.
 *
 * This is the ONE side effect that switching locations has, and it must write
 * BOTH stores: `getLocation()` (apiAdapter) reads localStorage, while
 * CallAheadForm and WalkInForm read the cookie. Writing only one leaves the
 * guest list showing one restaurant while the add form creates parties at the
 * other — silently, and only visible a day later.
 *
 * Synchronous on purpose: callers rely on a subsequent fetch, which resolves
 * the location at call time, seeing the new value.
 */
export function setActiveLocation(location: string): void {
  if (typeof window === 'undefined') return;
  if (!isKnownLocation(location)) {
    console.error(`[Location] refusing to activate unknown location: ${location}`);
    return;
  }
  try {
    localStorage.setItem('restaurantLocation', location);
  } catch {
    // Private mode / storage disabled — the cookie below still carries it.
  }
  document.cookie = `restaurantLocation=${location}; path=/; max-age=31536000`;
}

/** The /planner route for a location, e.g. "/planner/mimosa-franklin". */
export function plannerPathFor(location: string): string | null {
  const slug = LOCATION_TO_SLUG[location];
  return slug ? `/planner/${slug}` : null;
}

/**
 * Try to determine the restaurant location from the current URL path.
 *
 * We look at the first segment after the leading slash:
 *   /mimosa-brookfield        -> Mimosa Brookfield
 *   /mimosa-franklin/call-ahead -> Mimosa Franklin
 *
 * If the segment matches one of our known slugs return the corresponding
 * display name.  Otherwise return null so the caller can fall back to other
 * mechanisms (e.g. localStorage).
 */
export function getLocationFromPath(): string | null {
  if (typeof window === 'undefined') return null;

  const segments = window.location.pathname.split('/').filter(Boolean);
  if (segments.length === 0) return null;

  const slug = segments[0].toLowerCase();
  return SLUG_TO_LOCATION[slug] || null;
} 